import type { FC, MutableRefObject } from 'react';
import { useCallback } from 'react';
import { Show } from '@components/Show';
import { Spinner } from '@components/Spinner';
import type { LibraryBlockTypeForListPage } from '@ContractBuilder/types';
import { InfiniteLoader } from '@features/infinite-loader';
import type { Table } from '@tanstack/react-table';
import clsx from 'clsx';

import type { BlocksLibrarySearchInputProps } from './BlocksLibrarySearchInput';
import { BlocksLibrarySearchInput } from './BlocksLibrarySearchInput';
import { BlocksNotFound } from './BlocksNotFound';
import { LibraryBlock } from './LibraryBlock';

export interface BlockLibraryListProps extends Omit<BlocksLibrarySearchInputProps, 'documentType'> {
  blocks: LibraryBlockTypeForListPage[];
  className?: string;
  listRef: MutableRefObject<HTMLDivElement | null>;
  total: number;
  onInsert: (blockLibraryId: string, block: LibraryBlockTypeForListPage | null) => Promise<void>;
  onLoadMoreItems: (previouslyLoadedItems: LibraryBlockTypeForListPage[]) => void;
  isFetching: boolean;
  table: Table<LibraryBlockTypeForListPage>;
  previewBlockId?: string;
  onPreviewToggle: (blockId: string) => void;
}

export const BlocksLibraryList: FC<BlockLibraryListProps> = ({
  blocks,
  className,
  total,
  listRef,
  onInsert,
  onLoadMoreItems,
  table,
  isClause,
  previewBlockId,
  onPreviewToggle,
  ...blocksLibrarySearchInputProps
}) => {
  const renderBlock = useCallback(
    (block: LibraryBlockTypeForListPage) => {
      const tableRow = table.getRow(block.id, true);

      if (!tableRow) {
        return null;
      }

      return (
        <LibraryBlock
          isClause={isClause}
          key={tableRow.id}
          block={block}
          onBlockInsert={() => onInsert(block.id, block)}
          tableRow={tableRow}
          previewBlockId={previewBlockId}
          onPreviewToggle={onPreviewToggle}
        />
      );
    },
    [isClause, onInsert, table, previewBlockId, onPreviewToggle],
  );

  const hasBlocks = blocks.length > 0;

  return (
    <div className="text-info-700 flex flex-col gap-2 text-xs!">
      <BlocksLibrarySearchInput table={table} isClause={isClause} {...blocksLibrarySearchInputProps} />
      <div className={clsx('max-h-[324px] overflow-y-auto', className)} ref={listRef}>
        <Show when={hasBlocks}>
          <InfiniteLoader data={blocks} total={total} renderItem={renderBlock} onLoadMoreItems={onLoadMoreItems} />
          <Show when={blocksLibrarySearchInputProps.isFetching}>
            <div className="border-info-200 bg-info-200 ml-auto flex cursor-pointer items-center justify-center rounded-sm border py-5 transition">
              <Spinner />
            </div>
          </Show>
        </Show>
        <Show when={!hasBlocks}>
          <BlocksNotFound isClause={isClause} />
        </Show>
      </div>
    </div>
  );
};
